import React, { memo, useEffect, useState } from 'react';
import moment from 'moment';

import PageComponent from '../components/PageComponent';
import CustomSelect from '../components/GeneralForm/CustomSelect';
import CustomCard from '../components/GeneralForm/CustomCard';
import MultiRoleDatePicker from '../components/GeneralForm/MultiRoleDatePicker';
import CustomButton from '../components/GeneralForm/CustomButton';
import { DATE_FORMAT_YMD } from '../constants/dateFormatsConst';
import { showMsg } from '../services/library';
import CustomCheckbox from '../components/GeneralForm/CustomCheckbox';
import AutoEditService from '../services/autoEditService';
import SettingService from '../services/settingService';
import { hasActionPermission } from '../generalUtils/rbacUtils';
import {
  ACTION_SLUG_RETROOPERATIONS_REPROCESSBG,
  ACTION_SLUG_RETROOPERATIONS_REMOVECOMPOSITIONS
} from '../constants/actionSlugConst';
import ConfirmationModal from '../components/GeneralForm/ConfirmationModal';
import CommonService from '../services/commonService';

const DATE_REGION = [
  { label: 'Less Than', value: '<' },
  { label: 'More Than', value: '>' },
  { label: 'Less Than or Equal to', value: '<=' },
  { label: 'More Than or Equal to', value: '>=' },
  { label: 'Equal to', value: '=' }
];

const VEHICLE_TYPE = [
  { label: 'All Vehicles', value: null },
  { label: 'Pre Owned Vehicles', value: 'USED' },
  { label: 'New Vehicles', value: 'NEW' }
];

const ReprocessBg = ({ dealerDetails, setDealerDetails, refreshKey, handleLayoutLoader }) => {
  const [bgStartDate, setBgStartDate] = useState('');
  const [bgDateRegion, setBgDateRegion] = useState(DATE_REGION[0]);
  const [isPrelim, setIsPrelim] = useState(false);
  const [isOldVeh, setIsOldVeh] = useState(false);

  const [exteriorList, setExteriorList] = useState([]);
  const [interiorList, setInteriorList] = useState([]);
  const [selectedExterior, setSelectedExterior] = useState([]);
  const [selectedInterior, setSelectedInterior] = useState([]);
  const [vehicleType, setVehicleType] = useState(VEHICLE_TYPE[0]);
  const [isDisable, setDisable] = useState(false);

  const getMavisSetting = async () => {
    handleLayoutLoader(true);

    try {
      const dealerShotsRes = await SettingService.allCompositionMavisSettings({
        dealer_id: dealerDetails?.value
      });
      if (dealerShotsRes?.status) {
        const allowedBgShots = (dealerShotsRes?.data || []).map(name => ({
          ...name,
          customColor: name?.dm_removebg === 1 && (name?.dm_bgid_new || name?.dm_bgid_used) ? '#228e3b' : ''
        }));
        setExteriorList(allowedBgShots?.filter(name => name.name_crop === 'e'));
        setInteriorList(allowedBgShots?.filter(name => name.name_crop === 'i'));
      } else {
        showMsg(dealerShotsRes?.messages, 'error');
      }
    } catch (err) {
      showMsg(err, 'error');
    } finally {
      handleLayoutLoader(false);
    }
  };

  const resetBgState = () => {
    setSelectedExterior([]);
    setSelectedInterior([]);
    setBgStartDate('');
    setIsPrelim(false);
    setIsOldVeh(false);
    setBgDateRegion(DATE_REGION[0]);
    setVehicleType(VEHICLE_TYPE[0]);
  };

  useEffect(() => {
    resetBgState();
    getMavisSetting();
  }, [refreshKey]);

  useEffect(() => {
    const shouldDisable = vehicleType?.value === 'NEW';
    setDisable(shouldDisable);

    if (shouldDisable) {
      setIsPrelim(false);
    }
  }, [vehicleType]);

  const validationFn = () => {
    const shotsId = [...selectedExterior, ...selectedInterior].map(item => item.value).join(',');
    const oldVehVal = isOldVeh || '';
    const dateRegionVal = bgDateRegion?.value || '';
    const date = bgStartDate ? moment(bgStartDate).format(DATE_FORMAT_YMD) : '';
    const vehicleTypeVal = vehicleType?.value || null;

    if (!shotsId) {
      showMsg('At least one Exterior or Interior shot must be selected.');
      return;
    }

    if (window.confirm('Would you like to proceed with the images?')) {
      return {
        shots: shotsId,
        dealer: dealerDetails?.value,
        date,
        isPrelim: isPrelim || '',
        oldVehVal,
        dateRegionVal,
        vehicleType: vehicleTypeVal
      };
    }
  };

  const startProcessingReprocessBGFn = async () => {
    const bgObj = validationFn();
    if (bgObj) {
      try {
        const response = await AutoEditService.reprocessBG(bgObj);
        if (response.status) {
          setDealerDetails({ ...dealerDetails, processingFlag: 3 });
          showMsg(response.message, 'success');
        } else {
          showMsg(response.errors, 'error');
        }
      } catch (error) {
        showMsg(error, 'error');
      }
    }
  };

  const startProcessingVDSReprocessBGFn = async () => {
    const bgObj = validationFn();
    if (bgObj) {
      try {
        const response = await AutoEditService.reprocessVDSBG(bgObj);
        if (response.status) {
          setDealerDetails({ ...dealerDetails, processingFlag: 3 });
          showMsg(response.message, 'success');
        } else {
          showMsg(response.errors, 'error');
        }
      } catch (error) {
        showMsg(error, 'error');
      }
    }
  };

  return (
    <>
      <div className="row mt-3">
        <div className="col-12 col-md-6 col-lg-6">
          <span className="col-6 col-md-3 col-lg-3 p-0">BG Retro Date</span>
          <div className="d-flex">
            {bgStartDate && (
              <CustomSelect
                options={DATE_REGION}
                selected={bgDateRegion}
                onChange={setBgDateRegion}
                placeholder="region"
                containerClass="w-50 mr-2"
                className="team-select"
              />
            )}
            <MultiRoleDatePicker
              className="col-6 col-md-3 col-lg-3 p-0"
              selected={bgStartDate}
              onChange={e => setBgStartDate(e)}
            />
          </div>
        </div>
        <div className="col-12 col-md-6 col-lg-6">
          <CustomCheckbox
            name="isPrelim"
            id="isPrelim"
            checked={isPrelim}
            onChange={e => setIsPrelim(e.target.checked)}
            labelName="Prelim Vehicle Process?"
            disabled={isDisable}
          />
          <CustomCheckbox
            name="isOldVeh"
            id="isOldVeh"
            checked={isOldVeh}
            onChange={e => setIsOldVeh(e.target.checked)}
            labelName="Old Vehicle Process?"
          />
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12 col-md-12 col-lg-12">
          <span className="col-6 col-md-3 col-lg-3 p-0">Vehicle Type</span>
          <div className="d-flex">
            <CustomSelect
              options={VEHICLE_TYPE}
              selected={vehicleType}
              onChange={setVehicleType}
              placeholder="vehicle type"
              containerClass="w-25"
              className="vehicle-type-select"
            />
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12 col-md-6 col-lg-6">
          <span>Exterior</span>
          <CustomSelect
            options={exteriorList}
            selected={selectedExterior}
            onChange={setSelectedExterior}
            isMulti
            placeholder="Select exterior(s)"
            className="team-select"
          />
        </div>
        <div className="col-12 col-md-6 col-lg-6">
          <span>Interior</span>
          <CustomSelect
            options={interiorList}
            selected={selectedInterior}
            onChange={setSelectedInterior}
            isMulti
            placeholder="Select interior(s)"
            className="team-select"
          />
        </div>

        <div className="col-12 mt-3">
          <h6 className="text-info">
            <b>Note :</b>
          </h6>
          <div className="mb-0 row">
            <div className="col-lg-6 col-12">
              <h6 className="text-info">Background Cutout:</h6>
              <ul>
                <li>
                  Enable the ‘Remove Background’ option in Master Compositions for the selected composition.
                </li>
                <li>Background should be selected from Mavis Settings.</li>
              </ul>
            </div>
            <div className="col-lg-6 col-12">
              <h6 className="text-info">Revert to Original Image:</h6>
              <ul>
                <li>
                  Enable the ‘Remove Background’ option in Master Compositions for the selected composition.
                </li>
                <li>{'No Background selected from Mavis Settings.'}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12 col-md-6 col-lg-6 mt-3">
          <CustomButton
            className="mr-2"
            disabled={dealerDetails.processingFlag > 0}
            color="primary"
            outline
            onClick={startProcessingReprocessBGFn}
          >
            ReprocessBG
          </CustomButton>
          <CustomButton
            className="mr-2"
            color="primary"
            disabled={dealerDetails.processingFlag > 0}
            outline
            onClick={startProcessingVDSReprocessBGFn}
          >
            VDS ReprocessBG
          </CustomButton>
          <CustomButton
            color="secondary"
            disabled={dealerDetails.processingFlag > 0}
            outline
            onClick={resetBgState}
          >
            Reset
          </CustomButton>
        </div>
      </div>
    </>
  );
};

const RemoveCompositions = ({ dealerDetails, setDealerDetails, refreshKey, handleLayoutLoader }) => {
  // const [removeShotsStartDate, setRemoveShotsStartDate] = useState('');
  // const [removeShotsDateRegion, setRemoveShotsDateRegion] = useState(DATE_REGION[0]);

  const [removeShotsList, setRemoveShotsList] = useState([]);
  const [selectedRemoveShots, setSelectedRemoveShots] = useState([]);
  const [showConfirmBox, setShowConfirmBox] = useState(false);

  const getShotsExceptPA = async () => {
    handleLayoutLoader(true);

    try {
      const dealerShotsRes = await SettingService.allCompositionsExceptPA({
        dealer_id: dealerDetails?.value
      });
      if (dealerShotsRes?.status) {
        setRemoveShotsList(dealerShotsRes?.data || []);
      } else {
        showMsg(dealerShotsRes?.messages, 'error');
      }
    } catch (err) {
      showMsg(err, 'error');
    } finally {
      handleLayoutLoader(false);
    }
  };

  const resetRemoveShotsStates = () => {
    setSelectedRemoveShots([]);
    // setRemoveShotsStartDate('');
    // setRemoveShotsDateRegion(DATE_REGION[0]);
  };

  useEffect(() => {
    resetRemoveShotsStates();
    getShotsExceptPA();
  }, [refreshKey]);

  const removeCompositions = async () => {
    const removeShots = [...selectedRemoveShots].map(item => item.value).join(',');
    // const dateRegionVal = removeShotsDateRegion?.value || '';
    // const date = removeShotsStartDate ? moment(removeShotsStartDate).format(DATE_FORMAT_YMD) : '';
    try {
      const response = await AutoEditService.removeCompositions({
        dealerId: dealerDetails?.value,
        removeShots
        // dateRegionVal,
        // date
      });

      if (response.status) {
        setDealerDetails({ ...dealerDetails, processingFlag: 6 });
        showMsg(response.message, 'success');
      } else {
        showMsg(response.errors, 'error');
      }
    } catch (error) {
      showMsg(error, 'error');
    }
  };

  const startProcessingReprocessBGFn = async () => {
    if (selectedRemoveShots.length === 0) {
      showMsg('At least one shot must be selected.');
      return;
    }

    setShowConfirmBox(true);
  };

  return (
    <>
      {/* <div className="row mt-3">
        <div className="col-12 col-md-6 col-lg-6">
          <span className="col-6 col-md-3 col-lg-3 p-0">Remove Compositions Retro Date</span>
          <div className="d-flex">
            {removeShotsStartDate && (
              <CustomSelect
                options={DATE_REGION}
                selected={removeShotsDateRegion}
                onChange={setRemoveShotsDateRegion}
                placeholder="region"
                containerClass="w-50 mr-2"
                className="team-select"
              />
            )}
            <MultiRoleDatePicker
              className="col-6 col-md-3 col-lg-3 p-0"
              selected={removeShotsStartDate}
              onChange={e => setRemoveShotsStartDate(e)}
            />
          </div>
        </div>
      </div> */}

      <div className="row mt-3">
        <div className="col-12 col-md-6">
          <span>Compositions</span>
          <CustomSelect
            options={removeShotsList}
            selected={selectedRemoveShots}
            onChange={setSelectedRemoveShots}
            isMulti
            isClearable
            placeholder="Select Composition(s)"
          />
        </div>

        <div className="col-12 mt-3">
          <h6 className="text-danger">Note:</h6>
          <ul>
            <li>The selected composition images will be deleted from Import, Mavis and Export.</li>
            <li>This action is irreversible. Please confirm before removing any composition.</li>
          </ul>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12 col-md-6 col-lg-6 mt-3">
          <CustomButton
            className="mr-2"
            disabled={dealerDetails.processingFlag > 0}
            color="primary"
            outline
            onClick={startProcessingReprocessBGFn}
          >
            {dealerDetails.processingFlag > 0 ? (
              <>
                <i className="fa fa-spinner fa-spin mx-1 fa-thin fs-15" />
                Please wait, one of the processes is currently running...
              </>
            ) : (
              'Remove Compositions'
            )}
          </CustomButton>

          <CustomButton
            color="secondary"
            disabled={dealerDetails.processingFlag > 0}
            outline
            onClick={resetRemoveShotsStates}
          >
            Reset
          </CustomButton>
        </div>
      </div>

      {showConfirmBox && (
        <ConfirmationModal
          name="confirm_remove_compositions"
          isOpen={showConfirmBox}
          closeClick={() => setShowConfirmBox(false)}
          modalTitle="Confirm Remove Compositions"
          message={
            <>
              <p>
                This action will remove
                <strong> {[...selectedRemoveShots].map(item => item.label).join(', ')} </strong>
                images from all vehicles (Import, Mavis & Export) from <strong>{dealerDetails?.label}</strong>
                .
              </p>
              <p>This action is irreversible. Would you like to continue?`</p>
            </>
          }
          showTextConfirm
          confirmText="REMOVE-COMPOSITION"
          confirmTextClassName="text-danger"
          isCustomButton
          modalBackDrop="static"
          submitButtonName="Proceed"
          saveButtonClassName="text-success"
          closeButtonClassName="text-danger"
          closeButtonName="Cancel"
          saveClick={() => {
            setShowConfirmBox(false);
            removeCompositions();
          }}
          modalSize="md"
          isCentered={true}
        />
      )}
    </>
  );
};

const RetroOperations = props => {
  const { dealer } = props;
  const [dealerId, setDealerId] = useState(null);
  const [dealerReframeHeroVal, setDealerReframeHeroVal] = useState(0);
  const [dealerDetails, setDealerDetails] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setDealerDetails(dealer);
    if (dealer) {
      setDealerId(dealer?.value);
      setDealerReframeHeroVal(dealer?.processingFlag);
    }
  }, [dealer]);

  const hasBackgroundAccess = hasActionPermission([ACTION_SLUG_RETROOPERATIONS_REPROCESSBG]);
  const hasRemoveCompositionAccess = hasActionPermission([ACTION_SLUG_RETROOPERATIONS_REMOVECOMPOSITIONS]);

  const getDataCurrentReprocess = async () => {
    try {
      // const currentReprocess =
      await CommonService.getCurrentReprocess({ dealer_id: dealerId });
      // console.log('currentReprocess --->>>', currentReprocess);
    } catch (err) {
      console.error('ERROR: getCurrentReprocess ===>>> ', err);
    }
  };

  useEffect(() => {
    if (dealerId) {
      getDataCurrentReprocess();
    }
  }, [dealerId]);

  useEffect(() => {
    if (dealerReframeHeroVal > 0) {
      getDataCurrentReprocess();
    }
  }, [dealerReframeHeroVal]);

  return (
    <PageComponent isPageHeader={{ isvisible: true, onClick: () => setRefreshKey(prev => prev + 1) }}>
      {dealerDetails?.value && (
        <>
          <CustomCard title="Apply Background">
            {!hasBackgroundAccess ? (
              <div className="text-info p-3">
                You do not have permission to access the Background Process.
              </div>
            ) : (
              <ReprocessBg
                dealerDetails={dealerDetails}
                setDealerDetails={setDealerDetails}
                refreshKey={refreshKey}
                {...props}
              />
            )}
          </CustomCard>

          <CustomCard title="Remove Compositions">
            {!hasRemoveCompositionAccess ? (
              <div className="text-info p-3">You do not have permission to remove compositions.</div>
            ) : (
              <RemoveCompositions
                dealerDetails={dealerDetails}
                setDealerDetails={setDealerDetails}
                refreshKey={refreshKey}
                {...props}
              />
            )}
          </CustomCard>
        </>
      )}
    </PageComponent>
  );
};

RetroOperations.displayName = 'RetroOperations';
export default memo(RetroOperations);
